<template>
	<span class="UsersEdit">
      <span>
        <el-button v-if="showEditButton && !icon"
         @click="handleEditButton"
         size="mini">
          {{ $t("users.edit") }}
        </el-button>
        <em v-if="showEditButton && icon"
            class="el-icon-edit-outline UsersEdit-icon"
            :title="$t('users.edit')"
            @click="handleEditButton"></em>
        <el-button v-if="!isSubUser && !user.id" @click="handleAddUser" class="inline" type="primary">
          {{ $t("users.addUser") }}
        </el-button>
      </span>

      <el-dialog
          :append-to-body="true"
          :visible.sync="dialogVisible"
          :width="isNarrow ? '92%' : '720px'"
          custom-class="UsersEdit-dialog"
      >
        <template slot="title">
          <div class="UsersEdit-title">{{ pageTitle }}</div>
          <p class="UsersEdit-hint">{{ requiredHintParts.before }}<span v-if="requiredHintParts.marked" class="UsersEdit-requiredMark">*</span>{{ requiredHintParts.after }}</p>
        </template>
        <el-form :model="user" ref="userEdit" label-position="top">
          <p class="UsersEdit-section">{{ $t('users.sectionIdentity') }}</p>
          <el-row :gutter="16">
            <el-col :span="pairSpan">
              <el-form-item :label="$t('users.firstname')" :rules="[{ type: 'string', required: true, message: $t('validator.isRequired') }]" prop="firstname">
                <el-input v-model="user.firstname"></el-input>
              </el-form-item>
            </el-col>
            <el-col :span="pairSpan">
              <el-form-item :label="$t('profile.lastname')" :rules="[{ type: 'string', required: true, message: $t('validator.isRequired') }]" prop="lastname">
                <el-input v-model="user.lastname"></el-input>
              </el-form-item>
            </el-col>
          </el-row>
          <el-form-item :label="$t('users.email')" :rules="[{ type: 'email', required: true, message: $t('validator.validEmail') }]" prop="email">
            <el-input v-model="user.email"></el-input>
          </el-form-item>
          <el-row :gutter="16">
            <el-col :span="pairSpan">
              <el-form-item :label="$t('users.phone')" :rules="phoneRules" prop="phone">
                <el-input v-model="user.phone"></el-input>
              </el-form-item>
            </el-col>
            <el-col :span="pairSpan">
              <el-form-item :label="$t('users.mobile')">
                <el-input v-model="user.mobile"></el-input>
              </el-form-item>
            </el-col>
          </el-row>

          <div class="UsersEdit-sectionRow">
            <p class="UsersEdit-section">{{ $t('users.sectionContact') }}</p>
            <span class="UsersEdit-optional">{{ $t('users.optional') }}</span>
          </div>
          <el-form-item :label="$t('users.address')">
            <el-input v-model="user.address"></el-input>
          </el-form-item>
          <el-row :gutter="16">
            <el-col :span="tripleSpan">
              <el-form-item :label="$t('users.postalCode')">
                <el-input v-model="user.zipcode"></el-input>
              </el-form-item>
            </el-col>
            <el-col :span="tripleSpan">
              <el-form-item :label="$t('users.city')">
                <el-input v-model="user.city"></el-input>
              </el-form-item>
            </el-col>
            <el-col :span="tripleSpan">
              <el-form-item :label="$t('users.country')">
                <el-input v-model="user.country"></el-input>
              </el-form-item>
            </el-col>
          </el-row>
          <el-form-item :label="$t('users.price')">
            <el-input :placeholder="$t('users.placeholderPrice')" v-model="user.pricing"></el-input>
          </el-form-item>

          <div v-if="showRoleChoice">
            <p class="UsersEdit-section">{{ $t('users.sectionAccess') }}</p>
            <div class="UsersEdit-accessChoices" :class="{ 'UsersEdit-accessChoices--stack': isNarrow }">
              <button
                  v-for="role in roleChoices"
                  :key="role.value"
                  type="button"
                  class="UsersEdit-accessCard"
                  :class="{ 'is-selected': selectedRole === role.value }"
                  :aria-pressed="selectedRole === role.value ? 'true' : 'false'"
                  @click="selectRole(role.value)"
              >
                <span class="UsersEdit-accessTitle">{{ $t(role.label) }}</span>
              </button>
            </div>
            <p v-if="showMessageCreateAccess" class="UsersEdit-accessNote">{{ $t('users.accessPendingNote') }}</p>
          </div>
          <div v-if="showAccessSection">
            <p class="UsersEdit-section">{{ $t('users.sectionAccess') }}</p>
            <div class="UsersEdit-accessChoices" :class="{ 'UsersEdit-accessChoices--stack': isNarrow }">
              <button
                  type="button"
                  class="UsersEdit-accessCard"
                  :class="{ 'is-selected': contactOnly === true }"
                  :aria-pressed="contactOnly === true ? 'true' : 'false'"
                  @click="selectAccess(true)"
              >
                <span class="UsersEdit-accessTitle">{{ $t('users.accessContactOnly') }}</span>
                <span class="UsersEdit-accessHint">{{ $t('users.accessContactOnlyHint') }}</span>
              </button>
              <button
                  type="button"
                  class="UsersEdit-accessCard"
                  :class="{ 'is-selected': contactOnly === false }"
                  :aria-pressed="contactOnly === false ? 'true' : 'false'"
                  @click="selectAccess(false)"
              >
                <span class="UsersEdit-accessTitle">{{ $t('users.accessSpace') }}</span>
                <span class="UsersEdit-accessHint">{{ $t('users.accessSpaceHint') }}</span>
              </button>
            </div>
            <el-form-item v-if="contactOnly !== null && !contactOnly" :label="$t('users.role')">
              <el-select
                  :placeholder="$t('users.rolePlaceholder')"
                  v-model="selectedRole"
              >
                <el-option
                    :key="role.value"
                    :label="$t(role.label)"
                    :value="role.value"
                    style="text-transform:capitalize"
                    v-for="role in getSelectableRoles"
                ></el-option>
              </el-select>
            </el-form-item>
            <p v-if="showMessageCreateAccess" class="UsersEdit-accessNote">{{ $t('users.accessPendingNote') }}</p>
          </div>
        </el-form>
        <span class="dialog-footer" slot="footer">
          <el-button @click="handleCancelButton">{{ $t('users.cancel') }}</el-button>
          <el-button @click="onSubmit" type="primary">{{ submitLabel }}</el-button>
        </span>
      </el-dialog>
	</span>
</template>

<script>
import { mapGetters } from 'vuex'
import { createUser, updateUser } from '@/services/users'

export default {
  name: 'UsersEdit',
  inject: ['browserContext'],
  props: ['userProp', 'mini', 'icon'],
  data () {
    return {
      dialogVisible: false,
      user: this.userProp,
      selectedRole: null,
      contactOnly: null,
      showMessageCreateAccess: false,
      roleChoices: [
        { value: 'ROLE_SUB_USER', label: 'users.user' },
        { value: 'ROLE_SUB_ADMIN', label: 'users.admin' },
        { value: 'ROLE_CONTACT', label: 'users.contact' }
      ]
    }
  },
  async beforeMount () {
    if (this.userProp && this.userProp.roles) {
      const self = this
      this.getAvailableRoles.forEach(function (role) {
        if (self.userProp.roles.includes(role.value)) {
          self.selectedRole = role.value
        }
      }, self)
    }
    if (!this.selectedRole || this.selectedRole === 'ROLE_CONTACT') {
      this.contactOnly = true
    } else {
      this.contactOnly = false
    }
    if (this.showRoleChoice && !this.isKnownAdminRole(this.selectedRole)) {
      this.selectedRole = 'ROLE_SUB_USER'
    }
  },

  computed: {
    ...mapGetters('users', ['getAvailableRoles', 'getSelectableRoles']),
    ...mapGetters(['isMainAccount', 'isSubAdmin', 'isSubUser', 'hasMultiUserAdmin']),
    pageTitle () {
      return this.user.id ? this.$t('users.editUser') : this.$t('users.createNewUser')
    },
    submitLabel () {
      return this.user && this.user.id ? this.$t('users.saveUser') : this.$t('users.createUserSubmit')
    },
    requiredHintParts () {
      const text = this.$t('users.requiredHint')
      const index = text.indexOf('*')
      if (index === -1) {
        return { before: text, after: '', marked: false }
      }
      return {
        before: text.slice(0, index),
        after: text.slice(index + 1),
        marked: true
      }
    },
    isNarrow () {
      return this.browserContext.down('md')
    },
    pairSpan () {
      return this.isNarrow ? 24 : 12
    },
    tripleSpan () {
      return this.isNarrow ? 24 : 8
    },
    canAssignRole () {
      return !this.isSubUser && !(this.user && this.user.mainAccount)
    },
    showRoleChoice () {
      return this.canAssignRole && this.hasMultiUserAdmin === true
    },
    showAccessSection () {
      return this.canAssignRole && this.hasMultiUserAdmin !== true && !!(this.connectedUser && this.connectedUser.multiAccounts)
    },
    ownerAdDataCode () {
      const data = this.$store.getters.getActiveDataByCode('OWNER_AD_DATA_CODE')
      if (!data || data.active === false) {
        return null
      }
      return data.value
    },
    phoneRequired () {
      return this.hasMultiUserAdmin === true
        && !!this.selectedRole
        && this.selectedRole !== 'ROLE_CONTACT'
        && this.ownerAdDataCode === 'TELEPHONE_A_AFFICHER'
    },
    phoneRules () {
      if (!this.phoneRequired) {
        return []
      }
      return [{ required: true, message: this.$t('validator.isRequired') }]
    },
    showEditButton () {
      const user = this.user
      if (!user || !user.id) {
        return false
      }

      return this.isMainAccount
        || (this.isSubAdmin && !user.mainAccount && (user.roles && !user.roles.includes('ROLE_SUB_ADMIN')))
        || user.username === this.connectedUser.username
        || (user.roles && user.roles.includes('ROLE_CONTACT'))
    },
    connectedUser () {
      return this.$store.getters.getUser
    },
    sitimmoUser () {
      return this.$store.getters.getSitimmoUser
    }
  },
  watch: {
    contactOnly: function (value) {
      if (this.hasMultiUserAdmin) {
        return
      }
      if (value) {
        this.selectedRole = 'ROLE_CONTACT'
        this.showMessageCreateAccess = false
      } else if (!value && this.selectedRole === 'ROLE_CONTACT') {
        this.selectedRole = 'ROLE_SUB_USER'
        this.showMessageCreateAccess = true
      }
    }
  },
  methods: {

    async onSubmit () {
      if (this.$ga) {
        this.$ga.event('mes_comptes', 'clic', 'confirmer-edition', this.sitimmoUser.id)
      }
      this.$refs.userEdit.validate(async (valid) => {
        if (valid) {
          let result = null
          this.prepareRoleForSave()
          if (this.showMessageCreateAccess) {
            this.user.active = false
          }
          if (!this.user.id) {
            if (this.canAssignRole && this.selectedRole) {
              this.user.roles = [this.selectedRole]
            }
            result = await createUser(this.user)
            if (result.type === 'error') {
              return false
            }
          } else {
            const payload = {
              id: this.user.id,
              lastname: this.user.lastname,
              firstname: this.user.firstname,
              email: this.user.email,
              phone: this.user.phone,
              mobile: this.user.mobile,
              address: this.user.address,
              zipcode: this.user.zipcode,
              city: this.user.city,
              country: this.user.country,
              pricing: this.user.pricing,
              active: this.user.active
            }
            if (this.canAssignRole) {
              const roles = [...this.user.roles]
              this.getAvailableRoles.forEach(function (role) {
                const index = roles.indexOf(role.value)
                if (index > -1) {
                  roles.splice(index, 1)
                }
              }, this)
              if (this.selectedRole) {
                roles.push(this.selectedRole)
              }
              this.user.roles = roles
              payload.roles = roles
            }
            result = await updateUser(payload)
            if (result.type === 'error') {
              this.$message({ message: this.$t('error.found'), type: 'error' })
              this.loading = false
              return false
            }
          }
          this.handleUpdateMessage(result)
          this.$emit('fetchList')
          this.dialogVisible = false
        }
      })
    },
    handleEditButton () {
      this.dialogVisible = true
      if (this.$ga) {
        this.$ga.event('mes_comptes', 'clic', 'editer', this.sitimmoUser.id)
      }
    },
    handleCancelButton () {
      if (this.$ga) {
        this.$ga.event('mes_comptes', 'clic', 'annuler-edition', this.sitimmoUser.id)
      }
      this.dialogVisible = false
    },
    isKnownAdminRole (role) {
      return role === 'ROLE_SUB_USER' || role === 'ROLE_SUB_ADMIN' || role === 'ROLE_CONTACT'
    },
    prepareRoleForSave () {
      if (this.showRoleChoice || this.showAccessSection || !this.canAssignRole) {
        return
      }
      this.selectedRole = 'ROLE_CONTACT'
      this.showMessageCreateAccess = false
    },
    selectRole (role) {
      if (this.hasMultiUserAdmin) {
        this.selectedRole = role
        return
      }
      const wasContact = !this.selectedRole || this.selectedRole === 'ROLE_CONTACT'
      this.selectedRole = role
      if (role === 'ROLE_CONTACT') {
        this.showMessageCreateAccess = false
        return
      }
      this.showMessageCreateAccess = wasContact
    },
    selectAccess (contactOnly) {
      this.contactOnly = contactOnly
    },
    handleAddUser () {
      if (this.$ga) {
        this.$ga.event('mes_comptes', 'clic', 'ajouter_utilisateur', this.sitimmoUser.id)
      }
      this.user = {} // to have a new clean user
      this.dialogVisible = true
    },
    handleUpdateMessage (result) {
      if (result && (result.status === 200 || result.status === 201)) {
        if (this.showMessageCreateAccess) {
          this.$message({ message: this.$t('users.messageCreateAccess'), type: 'success' })
        } else {
          if (this.user.id) {
            this.$message({ message: this.$t('users.messageUpdate'), type: 'success' })
          } else {
            this.$message({ message: this.$t('users.messageCreate'), type: 'success' })
          }
        }
      } else {
        this.$message({ message: this.$t('users.messageError'), type: 'error' })
      }
    }
  }
}
</script>

<style lang="scss" slot-scope="">
    @import "@/theme/_bootstrap.scss";

    .UsersEdit-icon {
      cursor: pointer;
      font-size: 16px;
    }

    .UsersEdit-title {
      color: $black;
      font-family: $font-family-sans-serif-alt;
      font-size: $font-size-md-alt;
      font-weight: $font-weight-bold;
      line-height: 1.3;
    }

    .UsersEdit-hint {
      margin: map-get($spacers, 1) 0 0;
      color: $black-variant;
      font-size: $font-size-sm;
      font-weight: normal;
    }

    .UsersEdit-requiredMark {
      color: $red;
    }

    .UsersEdit-sectionRow {
      display: flex;
      align-items: center;
      justify-content: space-between;
    }

    .UsersEdit-section {
      margin: map-get($spacers, 2) 0 map-get($spacers, 3);
      color: $black-variant;
      font-family: $font-family-sans-serif-alt;
      font-size: $font-size-xs;
      font-weight: $font-weight-bold;
      letter-spacing: 0.04em;
      text-transform: uppercase;
    }

    .UsersEdit-optional {
      margin-bottom: map-get($spacers, 3);
      padding: 2px map-get($spacers, 2);
      border-radius: 10px;
      background: $blue-pastel;
      color: $text-muted;
      font-size: $font-size-xs;
    }

    .UsersEdit-accessChoices {
      display: flex;
      margin-bottom: map-get($spacers, 3);
    }

    .UsersEdit-accessCard {
      flex: 1;
      padding: map-get($spacers, 3);
      border: 1px solid #d7e0e2;
      border-radius: $border-radius;
      background: #fff;
      color: $black;
      text-align: left;
      cursor: pointer;

      & + & {
        margin-left: map-get($spacers, 3);
      }

      &.is-selected {
        border-color: $green;
        background: $blue-pastel;

        .UsersEdit-accessTitle {
          color: $green;
        }
      }
    }

    .UsersEdit-accessChoices--stack {
      flex-direction: column;

      .UsersEdit-accessCard + .UsersEdit-accessCard {
        margin-left: 0;
        margin-top: map-get($spacers, 3);
      }
    }

    .UsersEdit-accessTitle {
      display: block;
      font-weight: $font-weight-bold;
    }

    .UsersEdit-accessHint {
      display: block;
      margin-top: map-get($spacers, 1);
      color: $black-variant;
      font-size: $font-size-sm;
    }

    .UsersEdit-accessNote {
      margin: 0;
      color: $black-variant;
      font-size: $font-size-sm;
    }

    .UsersEdit-dialog {
      .el-select {
        width: 100%;
      }

      .el-form-item {
        margin-bottom: map-get($spacers, 3);
      }

      .el-form-item.is-required .el-form-item__label::before {
        display: none;
      }

      .el-form-item.is-required .el-form-item__label::after {
        content: '*';
        margin-left: map-get($spacers, 1);
        color: $red;
      }
    }
</style>
